import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Flex, Text } from '@chakra-ui/react'
import { Asset, Device, DeviceType } from '@leid/api-assets'
import { OrderItem } from '@leid/api-orders'

import { LeidButtonWhiteOutlined } from 'components/ui/Buttons/Button'
import { NewPadlockIcon } from 'components/ui/ItemCard/styles'
import { Loader } from 'components/ui/Loader'
import apiClient from 'helpers/clients/apiClient'
import { useCustomerInfo } from 'hooks/useCustomerInfo'
import { utcNow } from 'utils/dates'

import { getOrderItemFromDate, getOrderItemToDate } from '../utils/orderDates'

type Props = {
  orderIsLoading: boolean
  orderItem: OrderItem
}

const PinCode = (props: Props): JSX.Element | null => {
  const { orderIsLoading, orderItem } = props

  const { customerInfo } = useCustomerInfo()

  const { t } = useTranslation('common')

  const [isLoading, setIsLoading] = useState(false)
  const [hidePinCode, setHidePinCode] = useState(true)
  const [showPinCode, setShowPinCode] = useState(false)
  const [pinCode, setPinCode] = useState<string | undefined | null>(undefined)
  const [asset, setAsset] = useState<Asset | undefined>(undefined)

  useEffect(() => {
    if (orderIsLoading) {
      setHidePinCode(true)
      return
    }

    getAsset(orderItem)
  }, [orderItem, orderIsLoading])

  useEffect(() => {
    if (!showPinCode) {
      return
    }

    getLockPinCode()

    const timeout = setTimeout(() => {
      setPinCode(undefined)
      setShowPinCode(false)
    }, 600000)

    return () => clearTimeout(timeout)
  }, [showPinCode])

  const getAsset = async (orderItem: OrderItem) => {
    if (!orderItem.assetId) {
      setAsset(undefined)
      setHidePinCode(true)
      return
    }
    if (orderItem.rentedTo && orderItem.rentedTo <= utcNow()) {
      setAsset(undefined)
      setHidePinCode(true)
      return
    }

    setIsLoading(true)
    const response = await apiClient.getAsset(orderItem.assetId)
    if (!response.success || !response.data) {
      setAsset(undefined)
      setHidePinCode(false)
      setIsLoading(false)
      return
    }
    const asset = response.data
    const assetHasLockDevice = asset.devices?.some((device: Device) => device.deviceType === DeviceType.PADLOCK)

    if (assetHasLockDevice) {
      setAsset(asset)
      setHidePinCode(false)
    } else {
      setAsset(undefined)
      setHidePinCode(true)
    }

    setIsLoading(false)
  }

  const getLockPinCode = async () => {
    if (!orderItem.assetId) {
      return
    }

    if (orderItem.rentedTo && orderItem.rentedTo <= utcNow()) {
      return
    }

    if (!asset) {
      return
    }

    setIsLoading(true)

    const pinCodeResponse = await apiClient.getLockPin({
      assetId: asset.id,
      orderId: orderItem.orderId,
      orderitemId: orderItem.id,
      variance: 1,
      startDate: getOrderItemFromDate(orderItem) ?? orderItem.createdAt,
      endDate: getOrderItemToDate(orderItem) ?? utcNow(),
      customerId: customerInfo?.customer?.id ?? 0,
    })
    if (!pinCodeResponse.success || !pinCodeResponse.data) {
      setPinCode(null)
      setIsLoading(false)
      return
    }

    const pinCode = pinCodeResponse.data.pin_code

    setPinCode(pinCode)

    setIsLoading(false)
  }

  const toPrettifiedPin = (pin_code: string): string => {
    const prettified = pin_code?.replace(/(.{3})/g, '$1 ').trim()
    return prettified
  }

  if (hidePinCode) {
    return null
  }

  if (isLoading) {
    return (
      <Flex height="45px" width="100%" justifyContent="center" alignItems="center">
        <Loader margin={0} size="md" />
      </Flex>
    )
  }

  return (
    <Flex height="45px" width="100%" justifyContent="center" alignItems="center">
      {!showPinCode ? (
        <>
          <LeidButtonWhiteOutlined onClick={() => setShowPinCode(true)}>
            <NewPadlockIcon />
            {t('showPinCode')}
          </LeidButtonWhiteOutlined>
        </>
      ) : (
        <>
          {pinCode ? (
            <Flex justifyContent="center" alignItems="center" gridGap="4px" width="100%">
              <Text fontSize="md">
                {t('lockPinCode')} {toPrettifiedPin(pinCode)}
              </Text>
              <NewPadlockIcon color="black" />
            </Flex>
          ) : (
            <Flex justifyContent="center" alignItems="center" gridGap="12px" width="100%">
              <Text fontSize="xs" width="100%">
                {t('cannotGetPinCode')}
              </Text>
              <LeidButtonWhiteOutlined onClick={getLockPinCode}>
                <Text fontSize="xs">{t('tryAgain')}</Text>
              </LeidButtonWhiteOutlined>
            </Flex>
          )}
        </>
      )}
    </Flex>
  )
}

export default PinCode
